import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useInPhrase } from '../contexts/GeoCountryContext';
import Header from './Header';
import Footer from './Footer';
import SEOHead from './SEOHead';
import { FlaskConical, Shield, Award, Building2, Sparkles } from 'lucide-react';

const AboutPage = () => {
  const { currentLanguage, t } = useLanguage();
  const inPhrase = useInPhrase(currentLanguage);

  const content = {
    en: {
      title: "About PenLab Peptide",
      subtitle: "Leading the future of peptide research",
      mission: "Our Mission",
      missionText: "We are dedicated to providing the highest quality research peptides and compounds to the scientific community. Our mission is to advance biochemical research by offering stabilized, pre-mixed formulations that meet the most rigorous standards.",
      story: "Our Story",
      storyText: "Founded with a passion for scientific advancement, PenLab Peptide has become the leading provider of research peptides {loc}. We specialize in innovative stabilized pen formats that ensure product integrity and ease of use for researchers.",
      values: "Our Values",
      quality: "Quality First",
      qualityText: "Every product undergoes rigorous testing to ensure >99% purity and consistency.",
      innovation: "Innovation",
      innovationText: "We pioneer stabilized formulations and pre-mixed pen delivery systems.",
      integrity: "Scientific Integrity",
      integrityText: "All products are strictly for research purposes only.",
      stats: [
        { number: "143+", label: "Research Products" },
        { number: "99%+", label: "Purity Standard" },
        { number: "3-5", label: "Day Delivery" },
        { number: "100%", label: "Stabilized Formulas" }
      ]
    },
    es: {
      title: "Sobre PenLab Peptide",
      subtitle: "Liderando el futuro de la investigación de péptidos",
      mission: "Nuestra Misión",
      missionText: "Estamos dedicados a proporcionar péptidos de investigación de la más alta calidad a la comunidad científica. Nuestra misión es avanzar la investigación bioquímica ofreciendo formulaciones estabilizadas y premezcladas que cumplen los estándares más rigurosos.",
      story: "Nuestra Historia",
      storyText: "Fundada con una pasión por el avance científico, PenLab Peptide se ha convertido en el proveedor líder de péptidos de investigación {loc}. Nos especializamos en formatos de pluma estabilizados innovadores que garantizan la integridad del producto y facilidad de uso para investigadores.",
      values: "Nuestros Valores",
      quality: "Calidad Primero",
      qualityText: "Cada producto pasa por pruebas rigurosas para garantizar >99% de pureza y consistencia.",
      innovation: "Innovación",
      innovationText: "Somos pioneros en formulaciones estabilizadas y sistemas de entrega de plumas premezcladas.",
      integrity: "Integridad Científica",
      integrityText: "Todos los productos son estrictamente solo para fines de investigación.",
      stats: [
        { number: "143+", label: "Productos de Investigación" },
        { number: "99%+", label: "Estándar de Pureza" },
        { number: "3-5", label: "Días de Entrega" },
        { number: "100%", label: "Fórmulas Estabilizadas" }
      ]
    },
    ru: {
      title: "О компании PenLab Peptide",
      subtitle: "Мы формируем будущее исследований пептидов",
      mission: "Наша миссия",
      missionText: "Мы стремимся обеспечивать научное сообщество исследовательскими пептидами высочайшего качества. Наша цель, содействовать развитию биохимических исследований, предлагая стабилизированные готовые формулы, соответствующие самым строгим стандартам.",
      story: "Наша история",
      storyText: "Компания PenLab Peptide была основана из стремления к научному прогрессу и стала ведущим поставщиком исследовательских пептидов {loc}. Мы специализируемся на инновационных стабилизированных шприц-ручках, которые гарантируют сохранность продукта и удобство работы для исследователей.",
      values: "Наши ценности",
      quality: "Качество превыше всего",
      qualityText: "Каждый продукт проходит тщательное тестирование для обеспечения чистоты более 99% и стабильности.",
      innovation: "Инновации",
      innovationText: "Мы, пионеры в области стабилизированных формул и систем доставки в виде готовых шприц-ручек.",
      integrity: "Научная добросовестность",
      integrityText: "Вся продукция предназначена исключительно для научных исследований.",
      stats: [
        { number: "143+", label: "Исследовательских продуктов" },
        { number: "99%+", label: "Стандарт чистоты" },
        { number: "3-5", label: "Дней доставки" },
        { number: "100%", label: "Стабилизированных формул" }
      ]
    },
    de: {
      title: "Über PenLab Peptide",
      subtitle: "Wir gestalten die Zukunft der Peptidforschung",
      mission: "Unsere Mission",
      missionText: "Wir versorgen die wissenschaftliche Gemeinschaft mit hochwertigsten Forschungspeptiden. Unsere Mission ist es, die biochemische Forschung mit stabilisierten, vorgemischten Formulierungen voranzutreiben, die den höchsten Standards entsprechen.",
      story: "Unsere Geschichte",
      storyText: "Aus Leidenschaft für wissenschaftlichen Fortschritt gegründet, ist PenLab Peptide zum führenden Anbieter von Forschungspeptiden {loc} geworden. Wir sind auf innovative stabilisierte Pen-Formate spezialisiert, die Produktintegrität und Anwenderfreundlichkeit für Forscher sichern.",
      values: "Unsere Werte",
      quality: "Qualität an erster Stelle",
      qualityText: "Jedes Produkt durchläuft strenge Tests, um >99% Reinheit und Konsistenz zu gewährleisten.",
      innovation: "Innovation",
      innovationText: "Wir sind Pioniere stabilisierter Formulierungen und vorgemischter Pen-Liefersysteme.",
      integrity: "Wissenschaftliche Integrität",
      integrityText: "Alle Produkte sind ausschließlich zu Forschungszwecken bestimmt.",
      stats: [
        { number: "143+", label: "Forschungsprodukte" },
        { number: "99%+", label: "Reinheitsstandard" },
        { number: "3-5", label: "Tage Lieferung" },
        { number: "100%", label: "Stabilisierte Formulierungen" }
      ]
    },
    fr: {
      title: "À propos de PenLab Peptide",
      subtitle: "À la pointe de l'avenir de la recherche peptidique",
      mission: "Notre mission",
      missionText: "Nous nous engageons à fournir à la communauté scientifique des peptides de recherche de la plus haute qualité. Notre mission est de faire progresser la recherche biochimique en proposant des formulations stabilisées et pré-mélangées répondant aux normes les plus rigoureuses.",
      story: "Notre histoire",
      storyText: "Fondée par passion pour l'avancement scientifique, PenLab Peptide est devenu le principal fournisseur de peptides de recherche {loc}. Nous sommes spécialisés dans des formats de stylos stabilisés innovants qui garantissent l'intégrité du produit et la facilité d'utilisation pour les chercheurs.",
      values: "Nos valeurs",
      quality: "Qualité avant tout",
      qualityText: "Chaque produit subit des tests rigoureux pour garantir une pureté > 99 % et une constance.",
      innovation: "Innovation",
      innovationText: "Nous innovons avec des formulations stabilisées et des systèmes de stylos pré-mélangés.",
      integrity: "Intégrité scientifique",
      integrityText: "Tous les produits sont strictement destinés à la recherche.",
      stats: [
        { number: "143+", label: "Produits de recherche" },
        { number: "99%+", label: "Standard de pureté" },
        { number: "3-5", label: "Jours de livraison" },
        { number: "100%", label: "Formules stabilisées" }
      ]
    },
    it: {
      title: "Chi siamo - PenLab Peptide",
      subtitle: "Guidiamo il futuro della ricerca peptidica",
      mission: "La nostra missione",
      missionText: "Ci impegniamo a fornire alla comunità scientifica peptidi di ricerca della massima qualità. La nostra missione è far progredire la ricerca biochimica offrendo formulazioni stabilizzate e pre-miscelate che soddisfano gli standard più rigorosi.",
      story: "La nostra storia",
      storyText: "Fondata dalla passione per il progresso scientifico, PenLab Peptide è diventata il fornitore leader di peptidi per la ricerca {loc}. Siamo specializzati in formati di penne stabilizzate innovative che garantiscono l'integrità del prodotto e la facilità d'uso per i ricercatori.",
      values: "I nostri valori",
      quality: "Qualità prima di tutto",
      qualityText: "Ogni prodotto è sottoposto a test rigorosi per garantire una purezza >99% e costanza.",
      innovation: "Innovazione",
      innovationText: "Siamo pionieri di formulazioni stabilizzate e sistemi di penne pre-miscelate.",
      integrity: "Integrità scientifica",
      integrityText: "Tutti i prodotti sono esclusivamente per scopi di ricerca.",
      stats: [
        { number: "143+", label: "Prodotti di ricerca" },
        { number: "99%+", label: "Standard di purezza" },
        { number: "3-5", label: "Giorni di consegna" },
        { number: "100%", label: "Formule stabilizzate" }
      ]
    },
    pl: {
      title: "O PenLab Peptide",
      subtitle: "Tworzymy przyszłość badań nad peptydami",
      mission: "Nasza misja",
      missionText: "Naszym zadaniem jest dostarczanie społeczności naukowej najwyższej jakości peptydów badawczych. Misja: rozwijać badania biochemiczne oferując ustabilizowane, pre-mieszane formuły spełniające najwyższe standardy.",
      story: "Nasza historia",
      storyText: "Założona z pasji do postępu naukowego, PenLab Peptide stała się czołowym dostawcą peptydów badawczych {loc}. Specjalizujemy się w innowacyjnych ustabilizowanych penach, które gwarantują integralność produktu i łatwość użycia dla badaczy.",
      values: "Nasze wartości",
      quality: "Jakość przede wszystkim",
      qualityText: "Każdy produkt przechodzi rygorystyczne testy, aby zapewnić czystość >99% i powtarzalność.",
      innovation: "Innowacja",
      innovationText: "Jesteśmy pionierami stabilizowanych formuł i systemów pre-mieszanych penów.",
      integrity: "Rzetelność naukowa",
      integrityText: "Wszystkie produkty są przeznaczone wyłącznie do celów badawczych.",
      stats: [
        { number: "143+", label: "Produkty badawcze" },
        { number: "99%+", label: "Standard czystości" },
        { number: "3-5", label: "Dni dostawy" },
        { number: "100%", label: "Stabilizowane formuły" }
      ]
    },
    nl: {
      title: "Over PenLab Peptide",
      subtitle: "Voorop in de toekomst van peptide-onderzoek",
      mission: "Onze missie",
      missionText: "Wij leveren de wetenschappelijke gemeenschap onderzoekspeptiden van de hoogste kwaliteit. Onze missie is om biochemisch onderzoek te bevorderen door gestabiliseerde, voorgemengde formuleringen aan te bieden die voldoen aan de strengste normen.",
      story: "Ons verhaal",
      storyText: "Gefundeerd op een passie voor wetenschappelijke vooruitgang, is PenLab Peptide uitgegroeid tot de leidende leverancier van onderzoekspeptiden {loc}. We zijn gespecialiseerd in innovatieve gestabiliseerde pen-formaten die productintegriteit en gebruiksgemak voor onderzoekers garanderen.",
      values: "Onze waarden",
      quality: "Kwaliteit voorop",
      qualityText: "Elk product ondergaat strenge tests om >99% zuiverheid en consistentie te garanderen.",
      innovation: "Innovatie",
      innovationText: "Wij zijn pioniers in gestabiliseerde formuleringen en voorgemengde pen-toedieningssystemen.",
      integrity: "Wetenschappelijke integriteit",
      integrityText: "Alle producten zijn uitsluitend bestemd voor onderzoeksdoeleinden.",
      stats: [
        { number: "143+", label: "Onderzoeksproducten" },
        { number: "99%+", label: "Zuiverheidsstandaard" },
        { number: "3-5", label: "Dagen levering" },
        { number: "100%", label: "Gestabiliseerde formules" }
      ]
    },
    pt: {
      title: "Sobre a PenLab Peptide",
      subtitle: "Liderando o futuro da pesquisa em peptídeos",
      mission: "Nossa missão",
      missionText: "Comprometemo-nos a fornecer à comunidade científica peptídeos de pesquisa da mais alta qualidade. Nossa missão é avançar a pesquisa bioquímica oferecendo formulações estabilizadas e pré-misturadas que atendem aos padrões mais rigorosos.",
      story: "Nossa história",
      storyText: "Fundada com paixão pelo avanço científico, a PenLab Peptide tornou-se a principal fornecedora de peptídeos de pesquisa {loc}. Especializamo-nos em formatos de canetas estabilizadas inovadoras que garantem integridade do produto e facilidade de uso para pesquisadores.",
      values: "Nossos valores",
      quality: "Qualidade em primeiro lugar",
      qualityText: "Cada produto passa por testes rigorosos para garantir >99% de pureza e consistência.",
      innovation: "Inovação",
      innovationText: "Somos pioneiros em formulações estabilizadas e sistemas de canetas pré-misturadas.",
      integrity: "Integridade científica",
      integrityText: "Todos os produtos destinam-se exclusivamente a fins de pesquisa.",
      stats: [
        { number: "143+", label: "Produtos de pesquisa" },
        { number: "99%+", label: "Padrão de pureza" },
        { number: "3-5", label: "Dias de entrega" },
        { number: "100%", label: "Fórmulas estabilizadas" }
      ]
    }
  };

  const c = content[currentLanguage] || content.en;

  return (
    <div className="min-h-screen bg-capsule-bg">
      <SEOHead page="about" />
      <Header />

      {/* Hero Banner - Capsule 8 Design with branded image */}
      <div className="relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_6d651465-2998-4001-9651-0b3a9db3fd32/artifacts/vteec4th_joj10gk9_IMG_8961.webp')`,
          }}
        />
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/85 via-gray-800/80 to-capsule-teal-dark/70" />
        
        {/* Floating spheres */}
        <div className="absolute top-20 right-32 w-20 h-20 rounded-full bg-gradient-to-br from-capsule-rose-gold/30 to-capsule-salmon/20 blur-xl"></div>
        <div className="absolute bottom-20 right-48 w-16 h-16 rounded-full bg-gradient-to-br from-capsule-teal/30 to-capsule-aqua/20 blur-xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
          <div className="text-center max-w-3xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-6 border border-white/20">
              <Building2 className="w-4 h-4 text-capsule-aqua" />
              <span className="text-sm font-medium text-capsule-aqua">{t('about_our_company')}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              {c.title}
            </h1>
            <p className="text-xl text-transparent bg-clip-text bg-gradient-to-r from-capsule-teal to-capsule-aqua">
              {c.subtitle}
            </p>
          </div>
        </div>
        
        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 60L48 55C96 50 192 40 288 35C384 30 480 30 576 33.3C672 37 768 43 864 45C960 47 1056 45 1152 41.7C1248 38 1344 33 1392 30.8L1440 29V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0Z" fill="#E7EAED"/>
          </svg>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-gradient-to-r from-capsule-teal to-capsule-aqua py-12 -mt-1">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {c.stats.map((stat, index) => (
              <div key={index} className="text-center text-white">
                <div className="text-4xl md:text-5xl font-bold mb-2">{stat.number}</div>
                <div className="text-sm md:text-base opacity-90">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mission */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-capsule-teal text-sm font-semibold tracking-wider">PURPOSE</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 mt-2">{c.mission}</h2>
              <p className="text-gray-600 leading-relaxed text-lg">{c.missionText}</p>
            </div>
            <div className="glass-card rounded-3xl p-4">
              <img
                src="https://customer-assets.emergentagent.com/job_6d651465-2998-4001-9651-0b3a9db3fd32/artifacts/nxdjavof_WhatsApp%20Image%202026-02-07%20at%2013.29.48.jpeg"
                alt="PenLab Peptide Products"
                className="rounded-2xl shadow-lg w-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Story */}
      <div className="bg-capsule-bg-light py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-capsule-coral text-sm font-semibold tracking-wider">HISTORY</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 mt-2">{c.story}</h2>
            <p className="text-gray-600 leading-relaxed text-lg">{c.storyText.replace('{loc}', inPhrase)}</p>
          </div>
        </div>
      </div>

      {/* Values */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-capsule-teal text-sm font-semibold tracking-wider">{t('about_principles')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">{c.values}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="glass-card rounded-3xl p-8 text-center hover:shadow-glass-hover transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-capsule-teal to-capsule-aqua rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-teal-glow">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{c.quality}</h3>
              <p className="text-gray-600">{c.qualityText}</p>
            </div>
            <div className="glass-card rounded-3xl p-8 text-center hover:shadow-glass-hover transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <FlaskConical className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{c.innovation}</h3>
              <p className="text-gray-600">{c.innovationText}</p>
            </div>
            <div className="glass-card rounded-3xl p-8 text-center hover:shadow-glass-hover transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-capsule-coral to-capsule-salmon rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-coral-glow">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{c.integrity}</h3>
              <p className="text-gray-600">{c.integrityText}</p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AboutPage;
